import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tarefaService } from "@/services/api/tarefa.service";
import { pessoaService } from "@/services/api/PessoaService";
import { TarefaObservadorDTO } from "@/types/tarefa.type";
import { toast } from "sonner";

export function useTarefaObservadores(
  tarefaId: number,
  atribuidoAId?: number,
  criadoPorId?: number,
) {
  const queryClient = useQueryClient();

  const queryKeyObservadores = ["tarefa", tarefaId, "observadores"];
  const queryKeyPessoas = ["pessoas", "ativas"];

  const { data: observadores = [], isLoading: isObservadoresLoading } = useQuery({
    queryKey: queryKeyObservadores,
    queryFn: () => tarefaService.getObservadoresByTarefaId(tarefaId),
    enabled: !!tarefaId,
  });

  const { data: todasPessoas = [], isLoading: isPessoasLoading } = useQuery({
    queryKey: queryKeyPessoas,
    queryFn: async () => {
      const pessoas = await pessoaService.getAllPessoas();
      const pessoasAtivas = pessoas.filter((pessoa) => pessoa.ativo);

      return pessoasAtivas;
    },
    staleTime: 1000 * 60 * 15,
  });

  const pessoasDisponiveis = todasPessoas.filter(
    (pessoa) =>
      pessoa.ativo &&
      Number(pessoa.id) !== Number(atribuidoAId) &&
      Number(pessoa.id) !== Number(criadoPorId) &&
      !observadores.some((obs) => Number(obs.pessoa.id) === Number(pessoa.id)),
  );

  const addObservadorMutation = useMutation({
    mutationFn: (pessoasIds: number[]) => tarefaService.adicionarObservador(tarefaId, pessoasIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyObservadores });
      queryClient.invalidateQueries({ queryKey: ["tarefa-mensagens", tarefaId] });

      toast.success("Observador adicionado com sucesso");
    },
    onError: (error: Error | any) => {
      console.error(error);
    },
  });

  const removeObservadorMutation = useMutation({
    mutationFn: (observadorId: number) => tarefaService.removerObservador(tarefaId, observadorId),
    onMutate: async (observadorId) => {
      await queryClient.cancelQueries({ queryKey: queryKeyObservadores });
      const previousObservadores =
        queryClient.getQueryData<TarefaObservadorDTO[]>(queryKeyObservadores);

      if (previousObservadores) {
        queryClient.setQueryData<TarefaObservadorDTO[]>(
          queryKeyObservadores,
          previousObservadores.filter((obs) => obs.id !== observadorId),
        );
      }

      return { previousObservadores };
    },
    onError: (error: Error | any, _variables, context) => {
      if (context?.previousObservadores) {
        queryClient.setQueryData(queryKeyObservadores, context.previousObservadores);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeyObservadores });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tarefa-mensagens", tarefaId] });
      toast.success("Observador removido com sucesso");
    },
  });

  return {
    observadores,
    pessoasDisponiveis,
    isLoading: isObservadoresLoading || isPessoasLoading,
    addObservador: addObservadorMutation.mutateAsync,
    isAdding: addObservadorMutation.isPending,
    removeObservador: removeObservadorMutation.mutate,
    isRemoving: removeObservadorMutation.isPending,
  };
}
